"use client";

import React from "react";
import { ShieldCheck, Scale, FileText, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const credentialsData = [{
  icon: ShieldCheck,
  title: "Licensing & jurisdictions",
  description: "DJTD Consulting operates through fully licensed, state-regulated insurance professionals authorized to offer life insurance, fixed and fixed-indexed annuities, and income protection solutions across our approved jurisdictions.",
  points: ["State Department of Insurance compliant", "Authorized for multi-state resident and non-resident advisory", "Regular continuing education and ethics compliance"]
}, {
  icon: FileText,
  title: "Product availability & underwriting",
  description: "All illustrated policies, annuities, and living benefit guarantees depend on individual qualification, state availability, carrier underwriting criteria, and medical or financial eligibility.",
  points: ["Guarantees backed solely by issuing carrier financial strength", "Policy terms, surrender charges, and riders clearly outlined", "No guaranteed issue assumptions without formal underwriting"]
}, {
  icon: Scale,
  title: "Independent carrier representation",
  description: "We maintain an independent advisory posture. We are not tethered to proprietary sales quotas from any single institution, enabling objective evaluations across highly-rated life and annuity providers.",
  points: ["Broad access to established A-rated or higher carriers", "Objective comparisons without proprietary quotas", "Fiduciary-aligned, educational discovery process"]
}];
export default function Credentials() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="credentials-disclosures" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden text-foreground">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 md:px-12">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="max-w-3xl mb-12 md:mb-16">
          {/* Eyebrow Badge */}
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="outline" className="border-border bg-card text-foreground font-semibold uppercase tracking-wider text-xs px-3 py-1 rounded-full shadow-2xs">
              Licensing & Transparency
            </Badge>
          </div>

          {/* Heading */}
          <h2 className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-tight">
            Professional credentials & advisory disclosures
          </h2>

          {/* Architectural Anchor Dot Rule */}
          <div className="flex items-center gap-2 my-5">
            <span className="h-2 w-2 rounded-full bg-foreground shrink-0" />
            <span className="h-px w-28 bg-border shrink-0" />
          </div>

          {/* Lead Text */}
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear facts on our licensing, multi-carrier independence, and how we protect your long-term interests with transparent, educational guidance.
          </p>
        </motion.div>

        {/* Credentials 3-Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
          {credentialsData.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.title} variants={itemVariants} data-index={index}>
                <Card className="h-full bg-card border border-border rounded-xl p-6 sm:p-7 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200 hover:border-foreground/20 flex flex-col justify-between">
                  <div>
                    <CardHeader className="p-0 mb-5 flex flex-row items-center gap-4">
                      <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
                        <Icon className="size-6" />
                      </div>
                      <CardTitle className="font-default text-lg sm:text-xl font-semibold text-foreground leading-snug">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                        {item.description}
                      </p>
                      <ul className="space-y-3 pt-4 border-t border-border">
                        {item.points.map((point, pointIndex) => <li key={point} data-index={pointIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 font-medium">
                            <CheckCircle2 className="size-4 text-foreground shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Full-width Advisory & Legal Callout Box */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="rounded-xl border border-border bg-muted p-6 sm:p-8 md:p-10 shadow-2xs">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div className="space-y-3 max-w-3xl">
              <h3 className="font-default text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                <FileText className="size-4 shrink-0 text-foreground" />
                <span>Important educational notice & regulatory disclaimer</span>
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                DJTD Consulting is an independent financial education and insurance consulting practice. Information provided on this website, during educational sessions, and within strategy discussions is intended strictly for general educational and informational purposes. It should not be construed as formal legal, accounting, or tax advice.
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Annuity guarantees and life insurance death benefits rely exclusively on the financial strength and claims-paying ability of the issuing insurance company. Policy riders, surrender charges, participation rates, and payout caps vary by carrier and state of residence. Consult your certified tax professional or attorney regarding your specific legal and tax situation before executing any financial plan.
              </p>
            </div>

            <div className="shrink-0 flex items-start">
              <Button asChild variant="outline" size="default" className="border-border">
                <Link to="/FAQKnowledgeCenterPage">Knowledge center</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}